import React from "react";
import {
  Badge,
  Button,
  Card,
  Modal,
  Table,
} from "components/ui/BootstrapCompat";
import { useQuery } from "react-query";
import { FaGamepad } from "react-icons/fa";
import { FiCreditCard, FiHeart, FiInbox } from "react-icons/fi";
import {
  walletTransactionGetCount,
  walletTransactionGetMany,
} from "services/main/donate.service";
import WalletFilterBar from "./WalletFilterBar";
import {
  formatCurrency,
  getOrderAmount,
  getOrderBalanceAfter,
  getOrderBalanceBefore,
  getOrderCode,
  getOrderCountValue,
  getOrderCreatedAt,
  getOrderCurrency,
  getOrderDisplayCount,
  getOrderDonorName,
  getOrderId,
  getOrderLastDisplayedAt,
  getOrderMessage,
  getOrderPayload,
  getOrderSignedAmount,
  getOrderStatus,
  getOrderTitle,
  getOrderType,
  getOrderTypeLabel,
  getPaginationHasMore,
  normalizeTermgameOrders,
  PAGE_SIZE,
  TermgameOrder,
} from "./walletUtils";

type WalletTableSectionProps = {
  token: string;
  businessId: string;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  filterType: string;
  onFilterTypeChange: (value: string) => void;
  filterStatus: string;
  onFilterStatusChange: (value: string) => void;
  dateFrom: string;
  onDateFromChange: (value: string) => void;
  dateTo: string;
  onDateToChange: (value: string) => void;
  page: number;
  onPageChange: (page: number | ((prev: number) => number)) => void;
};

type WalletTransactionType =
  | "DONATION"
  | "WITHDRAWAL"
  | "ADJUSTMENT"
  | "TERMGAME_INCOME";

type WalletTransactionStatus = "PENDING" | "SUCCESS" | "FAILED" | "CANCELED";

const formatTransactionDate = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("lo-LA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatTransactionTime = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleTimeString("lo-LA", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTransactionVisual = (type: string) => {
  const normalized = String(type || "").toUpperCase();

  if (normalized.includes("GAME") || normalized.includes("TERMGAME")) {
    return {
      Icon: FaGamepad,
      label: "Game",
      className:
        "border-cyan-100 bg-cyan-50 text-cyan-600 dark:border-cyan-900/50 dark:bg-cyan-950/40 dark:text-cyan-300",
    };
  }

  if (normalized === "DONATION") {
    return {
      Icon: FiHeart,
      label: "Donate",
      className:
        "border-rose-100 bg-rose-50 text-rose-600 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300",
    };
  }

  return {
    Icon: FiCreditCard,
    label: "Transaction",
    className:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
};

function DetailField({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-[#edf1f6] bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
      <div className="text-[11px] font-black uppercase text-[#667085] dark:text-slate-400">
        {label}
      </div>
      <div
        className={`mt-1 min-w-0 break-words text-[13px] font-black text-[#101828] dark:text-slate-100 ${
          mono ? "font-mono" : ""
        }`}
      >
        {value || "--"}
      </div>
    </div>
  );
}

export default function WalletTableSection({
  token,
  businessId,
  searchTerm,
  onSearchTermChange,
  filterType,
  onFilterTypeChange,
  filterStatus,
  onFilterStatusChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  page,
  onPageChange,
}: WalletTableSectionProps) {
  const [selectedTransaction, setSelectedTransaction] =
    React.useState<TermgameOrder | null>(null);

  const requestFilters = React.useMemo(
    () => ({
      type:
        filterType !== "all"
          ? (filterType as WalletTransactionType)
          : undefined,
      status:
        filterStatus !== "all"
          ? (filterStatus as WalletTransactionStatus)
          : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [filterType, filterStatus, dateFrom, dateTo],
  );

  const {
    data: ordersResponse,
    isLoading,
    isRefetching,
    refetch: refetchList,
  } = useQuery(
    [
      "wallet-list",
      filterType,
      filterStatus,
      dateFrom,
      dateTo,
      page,
      businessId,
    ],
    async () => {
      try {
        return await walletTransactionGetMany({
          ...requestFilters,
          token,
          businessId,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          sortBy: "createdAt",
          order: "desc",
        });
      } catch (error: any) {
        console.error("Failed to fetch wallet list:", error);
        return { data: [], count: 0 };
      }
    },
    { enabled: !!token },
  );

  const { data: countResponse, refetch: refetchCount } = useQuery(
    ["wallet-count", filterType, filterStatus, dateFrom, dateTo, businessId],
    async () => {
      try {
        return await walletTransactionGetCount({
          ...requestFilters,
          token,
          businessId,
        });
      } catch (error: any) {
        console.error("Failed to fetch wallet count:", error);
        return { count: 0 };
      }
    },
    { enabled: !!token },
  );

  const data = React.useMemo(
    () => normalizeTermgameOrders(ordersResponse),
    [ordersResponse],
  );

  const explicitTotalCount = getOrderCountValue(
    countResponse ?? ordersResponse,
  );
  const hasMore = getPaginationHasMore(ordersResponse);
  const totalCount =
    explicitTotalCount ??
    (hasMore || data.length === PAGE_SIZE
      ? page * PAGE_SIZE + 1
      : (page - 1) * PAGE_SIZE + data.length);
  const totalPages =
    explicitTotalCount !== undefined
      ? Math.ceil(totalCount / PAGE_SIZE)
      : page + (hasMore || data.length === PAGE_SIZE ? 1 : 0);
  const safeTotalPages = Math.max(totalPages, 1);

  const filteredData = data.filter((order: TermgameOrder) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      String(getOrderTitle(order)).toLowerCase().includes(search) ||
      String(getOrderDonorName(order)).toLowerCase().includes(search) ||
      String(getOrderMessage(order)).toLowerCase().includes(search) ||
      String(order?.userName || "")
        .toLowerCase()
        .includes(search) ||
      String(order?.phone || "")
        .toLowerCase()
        .includes(search) ||
      String(getOrderCode(order)).toLowerCase().includes(search) ||
      String(getOrderType(order)).toLowerCase().includes(search) ||
      String(getOrderTypeLabel(order)).toLowerCase().includes(search) ||
      String(getOrderPayload(order)).toLowerCase().includes(search) ||
      String(getOrderId(order)).toLowerCase().includes(search)
    );
  });

  const handleExportCSV = () => {
    if (!filteredData || filteredData.length === 0) return;

    const headers = [
      "ລ/ດ",
      "ID",
      "Title",
      "Donor",
      "Code",
      "Type",
      "Message",
      "ຈຳນວນເງິນ (ກີບ)",
      "Balance Before",
      "Balance After",
      "Currency",
      "ສະຖານະ",
      "Display Count",
      "Last Displayed",
      "ວັນທີ",
    ];
    const rows = filteredData.map((order: TermgameOrder, i: number) => [
      (page - 1) * PAGE_SIZE + i + 1,
      getOrderId(order),
      getOrderTitle(order),
      getOrderDonorName(order),
      getOrderCode(order),
      getOrderTypeLabel(order),
      getOrderMessage(order) || getOrderPayload(order),
      getOrderSignedAmount(order),
      getOrderBalanceBefore(order),
      getOrderBalanceAfter(order),
      getOrderCurrency(order),
      getOrderStatus(order),
      getOrderDisplayCount(order),
      formatTransactionDate(getOrderLastDisplayedAt(order)),
      formatTransactionDate(getOrderCreatedAt(order)),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transaction-page${page}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "SUCCESS":
        return <Badge variant="success">ສຳເລັດ</Badge>;
      case "PENDING":
        return <Badge variant="warning">ລໍຖ້າ</Badge>;
      case "CANCELED":
        return <Badge variant="secondary">ຍົກເລີກ</Badge>;
      case "FAILED":
        return <Badge variant="danger">ລົ້ມເຫລວ</Badge>;
      default:
        return <Badge variant="light">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string, label: string) => {
    const normalized = String(type || "").toUpperCase();
    switch (normalized) {
      case "DONATION":
        return <Badge variant="primary">{label}</Badge>;
      case "WITHDRAWAL":
        return <Badge variant="dark">{label}</Badge>;
      case "TERMGAME_INCOME":
        return <Badge variant="info">{label}</Badge>;
      case "ADJUSTMENT":
        return <Badge variant="secondary">{label}</Badge>;
      default:
        return <Badge variant="light">{label || normalized || "--"}</Badge>;
    }
  };

  const renderMobileTransaction = (order: TermgameOrder, index: number) => {
    const orderId = getOrderId(order);
    const createdAt = getOrderCreatedAt(order);
    const signedAmount = getOrderSignedAmount(order);
    const isDebit = signedAmount < 0;
    const visual = getTransactionVisual(getOrderType(order));
    const Icon = visual.Icon;

    return (
      <button
        type="button"
        key={orderId || index}
        className="flex w-full min-w-0 items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 text-left shadow-[0_8px_20px_rgba(16,24,40,0.05)] transition hover:border-primary hover:bg-[#f8fbff] focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
        onClick={() => setSelectedTransaction(order)}
        aria-label={`Open ${visual.label} transaction details`}
      >
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg border ${visual.className}`}
          title={visual.label}
        >
          <Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-black text-[#344054] dark:text-slate-100">
            {formatTransactionTime(createdAt)}
          </span>
        </div>

        <span
          className={`max-w-[46%] flex-none truncate text-right text-[15px] font-black leading-tight ${
            isDebit ? "text-red-600" : "text-emerald-600"
          }`}
        >
          {isDebit ? "-" : "+"}
          {formatCurrency(Math.abs(getOrderAmount(order)))}
        </span>
      </button>
    );
  };

  return (
    <div className="min-w-0">
      <WalletFilterBar
        searchTerm={searchTerm}
        filterType={filterType}
        filterStatus={filterStatus}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={onSearchTermChange}
        onFilterTypeChange={(value) => {
          onFilterTypeChange(value);
          onPageChange(1);
        }}
        onFilterStatusChange={(value) => {
          onFilterStatusChange(value);
          onPageChange(1);
        }}
        onDateFromChange={(value) => {
          onDateFromChange(value);
          onPageChange(1);
        }}
        onDateToChange={(value) => {
          onDateToChange(value);
          onPageChange(1);
        }}
        onRefetch={() => {
          refetchList();
          refetchCount();
        }}
        onExport={handleExportCSV}
        disableExport={!filteredData || filteredData.length === 0}
      />

      <section className="md:hidden">
        <div className="mb-2 flex items-center justify-between gap-2 px-1">
          <h2 className="m-0 text-[15px] font-black text-[#101828] dark:text-slate-100">
            Transaction
          </h2>
          <span className="rounded-full bg-white px-3 py-1 text-[12px] font-black text-[#667085] shadow-sm dark:bg-slate-900 dark:text-slate-300">
            {totalCount} ລາຍການ
          </span>
        </div>

        <div className="space-y-2">
          {isLoading || isRefetching ? (
            Array.from({ length: 5 }, (_, index) => (
              <div
                key={`transaction-mobile-skeleton-${index}`}
                className="flex items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 shadow-[0_8px_20px_rgba(16,24,40,0.05)] dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="h-10 w-10 flex-none animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
                <div className="h-3 w-16 flex-1 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                <div className="h-4 w-24 flex-none animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
              </div>
            ))
          ) : filteredData && filteredData.length > 0 ? (
            filteredData.map(renderMobileTransaction)
          ) : (
            <div className="rounded-lg border border-dashed border-[#dbe4ef] bg-white px-4 py-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <FiInbox className="mx-auto h-6 w-6 text-slate-400" />
              <p className="m-0 mt-2 text-sm font-black text-[#475467] dark:text-slate-300">
                ບໍ່ພົບຂໍ້ມູນ
              </p>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-[#dbe4ef] bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <button
            className="btn btn-outline-primary btn-sm flex-1"
            disabled={page === 1}
            onClick={() => onPageChange((p) => p - 1)}
          >
            ‹
          </button>
          <div className="min-w-0 flex-[1.6] text-center text-[12px] font-black text-[#667085] dark:text-slate-300">
            Page {page} / {safeTotalPages}
          </div>
          <button
            className="btn btn-outline-primary btn-sm flex-1"
            disabled={page >= safeTotalPages || totalPages === 0}
            onClick={() => onPageChange((p) => p + 1)}
          >
            ›
          </button>
        </div>
      </section>

      {selectedTransaction && (
        <Modal
          show={!!selectedTransaction}
          onHide={() => setSelectedTransaction(null)}
          centered
          className="transaction-detail-modal-backdrop"
          dialogClassName="transaction-detail-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>Transaction Detail</Modal.Title>
          </Modal.Header>
          <Modal.Body className="transaction-detail-modal-body max-h-[70vh] overflow-y-auto">
            <div className="rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3 dark:border-slate-700 dark:bg-slate-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {getTypeBadge(
                      getOrderType(selectedTransaction),
                      getOrderTypeLabel(selectedTransaction),
                    )}
                    {getStatusBadge(getOrderStatus(selectedTransaction))}
                  </div>
                  <div className="text-[12px] font-bold text-[#667085] dark:text-slate-400">
                    {formatTransactionDate(
                      getOrderCreatedAt(selectedTransaction),
                    )}
                  </div>
                </div>
                <div className="flex-none text-right">
                  <div
                    className={`text-xl font-black leading-tight ${
                      getOrderSignedAmount(selectedTransaction) < 0
                        ? "text-red-600"
                        : "text-emerald-600"
                    }`}
                  >
                    {getOrderSignedAmount(selectedTransaction) < 0 ? "-" : "+"}
                    {formatCurrency(
                      Math.abs(getOrderAmount(selectedTransaction)),
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-[#667085] dark:text-slate-400">
                    {getOrderCurrency(selectedTransaction)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 grid gap-2">
              <DetailField
                label="Code"
                value={getOrderCode(selectedTransaction)}
                mono
              />
              <DetailField
                label="ID"
                value={getOrderId(selectedTransaction)}
                mono
              />
              <DetailField
                label="Title"
                value={getOrderTitle(selectedTransaction)}
              />
              <DetailField
                label="Donor"
                value={getOrderDonorName(selectedTransaction)}
              />
              <DetailField
                label="Message"
                value={
                  getOrderMessage(selectedTransaction) ||
                  getOrderPayload(selectedTransaction)
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <DetailField
                  label="Before"
                  value={formatCurrency(
                    getOrderBalanceBefore(selectedTransaction),
                  )}
                />
                <DetailField
                  label="After"
                  value={formatCurrency(
                    getOrderBalanceAfter(selectedTransaction),
                  )}
                />
              </div>
              <DetailField
                label="Last Displayed"
                value={formatTransactionDate(
                  getOrderLastDisplayedAt(selectedTransaction),
                )}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setSelectedTransaction(null)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Card className="hidden md:block">
        <Card.Header>Transaction</Card.Header>
        <div className="overflow-x-auto">
          <Table striped bordered hover size="sm" className="min-w-[1360px]">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left border-b w-[52px]">ລ/ດ</th>
                <th className="py-2 px-3 text-left border-b">ID</th>
                <th className="py-2 px-3 text-left border-b">Code</th>
                <th className="py-2 px-3 text-left border-b">Type</th>
                <th className="py-2 px-3 text-right border-b">ຈຳນວນເງິນ</th>
                <th className="py-2 px-3 text-right border-b">Before</th>
                <th className="py-2 px-3 text-right border-b">After</th>
                <th className="py-2 px-3 text-center border-b">ສະຖານະ</th>
                <th className="py-2 px-3 text-left border-b">ວັນທີ</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isRefetching ? (
                <tr>
                  <td colSpan={9} className="py-4 text-center">
                    ກຳລັງໂຫລດຂໍ້ມູນ...
                  </td>
                </tr>
              ) : filteredData && filteredData.length > 0 ? (
                filteredData.map((order: TermgameOrder, i: number) => {
                  const orderId = getOrderId(order);
                  const createdAt = getOrderCreatedAt(order);

                  return (
                    <tr key={orderId || i}>
                      <td className="py-2 px-3 border-b">
                        {(page - 1) * PAGE_SIZE + i + 1}
                      </td>
                      <td className="py-2 px-3 border-b">
                        <div className="max-w-[220px] truncate font-mono text-[11px] text-neutral-700">
                          {orderId || "--"}
                        </div>
                      </td>
                      <td className="py-2 px-3 border-b">
                        <div className="max-w-[160px] truncate text-[12px] font-mono">
                          {getOrderCode(order)}
                        </div>
                      </td>
                      <td className="py-2 px-3 border-b">
                        {getTypeBadge(
                          getOrderType(order),
                          getOrderTypeLabel(order),
                        )}
                      </td>
                      <td className="py-2 px-3 border-b text-right">
                        <span
                          className={`font-bold ${
                            getOrderSignedAmount(order) < 0
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {getOrderSignedAmount(order) < 0 ? "-" : "+"}
                          {formatCurrency(Math.abs(getOrderAmount(order)))}
                        </span>{" "}
                        {getOrderCurrency(order)}
                      </td>
                      <td className="py-2 px-3 border-b text-right text-[12px] text-neutral-700">
                        {formatCurrency(getOrderBalanceBefore(order))}
                      </td>
                      <td className="py-2 px-3 border-b text-right text-[12px] text-neutral-700">
                        {formatCurrency(getOrderBalanceAfter(order))}
                      </td>
                      <td className="py-2 px-3 border-b text-center">
                        {getStatusBadge(getOrderStatus(order))}
                      </td>

                      <td className="py-2 px-3 border-b text-[12px]">
                        {formatTransactionDate(createdAt)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-4 text-center">
                    ບໍ່ພົບຂໍ້ມູນ
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="flex flex-col gap-2 bg-white px-3 py-2 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-gray-500">
            ທັງໝົດ {totalCount} ລາຍການ
          </div>
          <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-1 md:pb-0">
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={page === 1}
              onClick={() => onPageChange(1)}
            >
              «
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={page === 1}
              onClick={() => onPageChange((p) => p - 1)}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
              )
              .reduce<(number | string)[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1)
                  acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`ellipsis-${idx}`} className="px-2">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => onPageChange(p as number)}
                  >
                    {p}
                  </button>
                ),
              )}
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={page >= safeTotalPages || totalPages === 0}
              onClick={() => onPageChange((p) => p + 1)}
            >
              ›
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={page >= safeTotalPages || totalPages === 0}
              onClick={() => onPageChange(safeTotalPages)}
            >
              »
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
